import React, { useState, useEffect } from 'react';
import { Box, Button, Container, Grid, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Seus componentes
import UserProfileCard from '../../components/UserProfileCard';
import UserInfoForm from '../../components/UserInfoForm';
import PasswordResetForm from '../../components/PasswordResetForm';
import DeleteUserCard from '../../components/DeleteUserCard';
import FeedbackSnackbar from '../../components/FeedbackSnackbar';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';

// Histórico separado
import UserAccessHistoryCard from '../../components/UserAccessHistoryCard';

// Serviço que busca histórico
import { fetchHistoricoAcessosPorPeriodo } from '../../services/historicoService';

function UserProfilePage({
  user,
  currentUserId,
  onBack,
  onDataChange,
  userService,
}) {
  const [userInfo, setUserInfo] = useState(user);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [feedback, setFeedback] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Histórico
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [sessoesUnicas, setSessoesUnicas] = useState([]);
  const [filtroPeriodo, setFiltroPeriodo] = useState('mes');

  const isCurrentUser =
    userInfo.uid === currentUserId || userInfo.id === currentUserId;

  useEffect(() => {
    const carregarAcessos = async () => {
      setIsLoadingHistory(true);
      try {
        const dados = await fetchHistoricoAcessosPorPeriodo(
          userInfo.uid,
          filtroPeriodo
        );
        setSessoesUnicas(dados);
      } catch (error) {
        setFeedback({
          open: true,
          message: `Erro ao carregar histórico: ${error.message}`,
          severity: 'error',
        });
      } finally {
        setIsLoadingHistory(false);
      }
    };
    carregarAcessos();
  }, [userInfo.uid, filtroPeriodo]);

  // Handlers
  const handleInputChange = (event) =>
    setUserInfo((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  const handlePasswordChange = (event) =>
    setPasswordData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  const handleCloseFeedback = (event, reason) => {
    if (reason === 'clickaway') return;
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  // Ações
  const handleUpdateInfo = async () => {
    try {
      await userService.updateUser(userInfo.uid, userInfo);
      onDataChange();
      setFeedback({
        open: true,
        message: 'Informações atualizadas com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      setFeedback({
        open: true,
        message: `Falha ao atualizar: ${error.message}`,
        severity: 'error',
      });
    }
  };

  const handleUpdatePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setFeedback({
        open: true,
        message: 'As senhas não coincidem.',
        severity: 'error',
      });
      return;
    }
    if (!passwordData.newPassword) {
      setFeedback({
        open: true,
        message: 'O campo de nova senha não pode estar vazio.',
        severity: 'error',
      });
      return;
    }
    try {
      await userService.updateUserPassword(userInfo.uid, passwordData.newPassword);
      onDataChange();
      setFeedback({
        open: true,
        message: 'Senha alterada com sucesso!',
        severity: 'success',
      });
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      setFeedback({
        open: true,
        message: `Falha ao redefinir senha: ${error.message}`,
        severity: 'error',
      });
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false);
    try {
      await userService.deleteUser(userInfo.uid);
      onDataChange();
      onBack();
    } catch (error) {
      setFeedback({
        open: true,
        message: `Falha ao excluir: ${error.message}`,
        severity: 'error',
      });
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        color: '#fff',
        width: '100%',
        maxWidth: '1600px',
        margin: '0 auto',
      }}
    >
      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        onClose={handleCloseFeedback}
      />
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        userName={userInfo.nome}
        isCurrentUser={isCurrentUser}
      />

      <Container maxWidth="lg">
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            mb: 3,
            borderColor: '#777',
            color: '#e0e0e0',
            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255, 255, 255, 0.08)' },
          }}
        >
          Voltar para a Lista
        </Button>

        <Grid container spacing={4}>
          <Grid item xs={20} md={4}>
            <Stack spacing={6} sx={{ height: '100%' }}>
              <UserProfileCard user={userInfo} />
              {/* 2. Instrua o card de histórico a crescer e ocupar o espaço restante */}
              <UserAccessHistoryCard
                sessoesUnicas={sessoesUnicas}
                isLoadingHistory={isLoadingHistory}
                filtroPeriodo={filtroPeriodo}
                setFiltroPeriodo={setFiltroPeriodo}
                sx={{ flexGrow: 1 }} // <--- Adicione esta prop!
              />
            </Stack>
          </Grid>

          <Grid item xs={12} md={8}>
            <Stack spacing={4}>
              <UserInfoForm
                userInfo={userInfo}
                onInputChange={handleInputChange}
                onUpdateInfo={handleUpdateInfo}
                isCurrentUser={isCurrentUser}
              />
              <PasswordResetForm
                passwordData={passwordData}
                onPasswordChange={handlePasswordChange}
                onUpdatePassword={handleUpdatePassword}
              />
              <DeleteUserCard
                onOpenDeleteDialog={() => setDeleteDialogOpen(true)}
                isCurrentUser={isCurrentUser}
              />
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default UserProfilePage;
